import React, { createContext, useContext, ReactNode } from 'react';

interface RuntimeConfig {
    publicDomain: string;
    livekitProjectId: string;
    roomShareBaseUrl?: string;
    recordingShareBaseUrl?: string;
    showFirstNameOnly: boolean;
}

export const defaultConfig: RuntimeConfig = {
    publicDomain: 'https://room-chat.typi.team',
    livekitProjectId: 'rm',
    showFirstNameOnly: false,
};

const RuntimeConfigContext = createContext<RuntimeConfig>(defaultConfig);

interface RuntimeConfigProviderProps {
    children: ReactNode;
    publicDomain?: string;
    livekitProjectId?: string;
    roomShareBaseUrl?: string;
    recordingShareBaseUrl?: string;
    showFirstNameOnly?: boolean;
}

export const RuntimeConfigProvider: React.FC<RuntimeConfigProviderProps> = ({
    children,
    publicDomain,
    livekitProjectId,
    roomShareBaseUrl,
    recordingShareBaseUrl,
    showFirstNameOnly,
}) => {
    const config: RuntimeConfig = {
        publicDomain: publicDomain || defaultConfig.publicDomain,
        livekitProjectId: livekitProjectId || defaultConfig.livekitProjectId,
        roomShareBaseUrl,
        recordingShareBaseUrl,
        showFirstNameOnly: showFirstNameOnly ?? defaultConfig.showFirstNameOnly,
    };

    return (
        <RuntimeConfigContext.Provider value={config}>
            {children}
        </RuntimeConfigContext.Provider>
    );
};

export const useRuntimeConfig = (): RuntimeConfig => {
    return useContext(RuntimeConfigContext);
};

export const getShowFirstNameOnly = (): boolean => {
    if (typeof window !== 'undefined') {
        const pageProps = (window as typeof window & {
            __NEXT_DATA__?: { props?: { pageProps?: Record<string, unknown> } };
        }).__NEXT_DATA__?.props?.pageProps;

        const value = pageProps?.showFirstNameOnly;
        if (typeof value === 'boolean') {
            return value;
        }
    }

    return process.env.NEXT_PUBLIC_SHOW_FIRST_NAME_ONLY === 'true';
};

export const buildDisplayName = (firstName: string | undefined, lastName: string | undefined, email: string): string => {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();
    const full = getShowFirstNameOnly() ? first : `${first} ${last}`.trim();
    return full || email.split('@')[0];
};
